import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  LinearProgress,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Download,
  InsertDriveFile,
  CheckCircle,
  Schedule,
  Search,
  Close,
  Stop,
  TableChart,
  PictureAsPdf,
  Image as ImageIcon,
  VideoLibrary,
  DataObject,
  LocalFireDepartment,
  Inventory,
  Public,
} from '@mui/icons-material';
import { useProcess } from '../context/ProcessContext';
import { NotificationContext } from '../context/NotificationContext';
import { useActivityLog } from '../context/ActivityLogContext';
import InterfaceHeader from '../components/InterfaceHeader';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

/* ── icon map per report name ── */
const REPORT_ICONS = {
  'US Items Detail': TableChart,
  'EU Image Links': ImageIcon,
  'US Image Links': ImageIcon,
  'Safavieh.com Not Published Rugs': Inventory,
  'Safavieh.com Not Published Non Rugs': Inventory,
  'Safavieh.com Published Rugs': Public,
  'Safavieh.com Published Non Rugs': Public,
  'Images Data': ImageIcon,
  'Photos DP Data': ImageIcon,
  'EU Items Detail Full Catalogue': Public,
  'Video Links': VideoLibrary,
  'Assembly Instructions Links': PictureAsPdf,
  'EU Assembly Instructions Links': PictureAsPdf,
  'Flammability Report Links': LocalFireDepartment,
};

const ExcelReports = ({ sourceFilter }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { processes, addProcess, updateProcess, removeProcess } = useProcess();
  const { addStructuredNotification } = useContext(NotificationContext);
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [lastUpdatedDates, setLastUpdatedDates] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Refs for async processing
  const intervalRefs = useRef({});
  const processIdRefs = useRef({});
  const downloadTriggeredRefs = useRef({});
  const uiProcessIdRefs = useRef({});

  const accentColor = sourceFilter === 'pdf' ? TOOL_COLORS.PDF.primary : TOOL_COLORS.EXCEL.primary;
  const accentHover = sourceFilter === 'pdf' ? TOOL_COLORS.PDF.hover : TOOL_COLORS.EXCEL.hover;

  const allReportEndpoints = [
    { name: 'US Items Detail', endpoint: API_ENDPOINTS.GENERAL_EXPORT_ITEMS_DETAIL, extension: 'csv', source: 'general' },
    { name: 'EU Image Links', endpoint: API_ENDPOINTS.GENERAL_EXPORT_EU_IMAGE_LINKS, extension: 'xlsx', source: 'general' },
    { name: 'US Image Links', endpoint: API_ENDPOINTS.GENERAL_EXPORT_US_IMAGE_LINKS, extension: 'xlsx', source: 'general' },
    { name: 'Safavieh.com Published Rugs', endpoint: API_ENDPOINTS.GENERAL_EXPORT_PUBLISHED_RUGS, extension: 'csv', source: 'general' },
    { name: 'Safavieh.com Published Non Rugs', endpoint: API_ENDPOINTS.GENERAL_EXPORT_PUBLISHED_NON_RUGS, extension: 'csv', source: 'general' },
    { name: 'Safavieh.com Not Published Rugs', endpoint: API_ENDPOINTS.GENERAL_EXPORT_UNPUBLISHED_RUGS, extension: 'xlsx', source: 'general' },
    { name: 'Safavieh.com Not Published Non Rugs', endpoint: API_ENDPOINTS.GENERAL_EXPORT_UNPUBLISHED_NON_RUGS, extension: 'xlsx', source: 'general' },
    { name: 'Images Data', endpoint: API_ENDPOINTS.GENERAL_EXPORT_IMAGES_DATA, extension: 'xlsx', source: 'general' },
    { name: 'Photos DP Data', endpoint: API_ENDPOINTS.GENERAL_EXPORT_PHOTOS_DP_DATA, extension: 'xlsx', source: 'general' },
    { name: 'EU Items Detail Full Catalogue', endpoint: API_ENDPOINTS.GENERAL_EXPORT_EU_ITEMS_DETAIL_FULL, extension: 'xlsx', source: 'general' },
    { name: 'Video Links', endpoint: API_ENDPOINTS.PDF_EXPORT_VIDEO_LINKS, extension: 'xlsx', source: 'pdf' },
    { name: 'Assembly Instructions Links', endpoint: API_ENDPOINTS.PDF_EXPORT_ASSEMBLY_LINKS, extension: 'xlsx', source: 'pdf' },
    { name: 'EU Assembly Instructions Links', endpoint: API_ENDPOINTS.PDF_EXPORT_EU_ASSEMBLY_LINKS, extension: 'xlsx', source: 'pdf' },
    { name: 'Flammability Report Links', endpoint: API_ENDPOINTS.PDF_EXPORT_FLAMMABILITY_LINKS, extension: 'xlsx', source: 'pdf' },
  ];

  const reportEndpoints = sourceFilter
    ? allReportEndpoints.filter(r => r.source === sourceFilter)
    : allReportEndpoints;

  const filteredReports = searchQuery
    ? reportEndpoints.filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : reportEndpoints;

  // Helper function to add logs
  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Fetch last updated dates on mount from both sources
  useEffect(() => {
    const fetchLastUpdatedDates = async () => {
      try {
        const [generalResponse, pdfResponse] = await Promise.all([
          apiCall(API_ENDPOINTS.GENERAL_EXPORT_LAST_UPDATED_DATES, { method: 'GET' }),
          apiCall(API_ENDPOINTS.PDF_EXPORT_LAST_UPDATED_DATES, { method: 'GET' })
        ]);

        const combinedDates = {};

        if (generalResponse.success && generalResponse.dates) {
          Object.assign(combinedDates, generalResponse.dates);
        }

        if (pdfResponse.success && pdfResponse.dates) {
          Object.assign(combinedDates, pdfResponse.dates);
        }

        setLastUpdatedDates(combinedDates);
      } catch (err) {
        console.error('Error fetching last updated dates:', err);
      }
    };

    fetchLastUpdatedDates();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.keys(intervalRefs.current).forEach(function (key) {
        if (intervalRefs.current[key]) {
          clearInterval(intervalRefs.current[key]);
          if (processIdRefs.current[key]) {
            clearIntervalRef(processIdRefs.current[key]);
          }
        }
      });
    };
  }, [clearIntervalRef]);

  const fetchStatus = async (processId, index) => {
    try {
      const source = reportEndpoints[index]?.source || 'general';
      const statusEndpoint = source === 'pdf'
        ? API_ENDPOINTS.PDF_EXPORT_STATUS(processId)
        : API_ENDPOINTS.GENERAL_EXPORT_STATUS(processId);
      const response = await apiCall(statusEndpoint);

      if (response.logs && response.logs.length > 0) {
        setLogs(processId, response.logs);
      }

      if (response.progressPercentage !== undefined) {
        setDownloadProgress(function (prev) {
          return Object.assign({}, prev, { [index]: response.progressPercentage });
        });
        updateActivityProcess(processId, { progress: response.progressPercentage });
      }

      if (response.status === 'completed') {
        if (intervalRefs.current[index]) {
          clearInterval(intervalRefs.current[index]);
          intervalRefs.current[index] = null;
          clearIntervalRef(processId);
        }

        if (!downloadTriggeredRefs.current[index]) {
          downloadTriggeredRefs.current[index] = true;
          await downloadFile(processId, index, response.fileName);
        } else {
          setDownloadProgress(function (prev) {
            var updated = Object.assign({}, prev);
            delete updated[index];
            return updated;
          });
        }
      } else if (response.status === 'error') {
        if (intervalRefs.current[index]) {
          clearInterval(intervalRefs.current[index]);
          intervalRefs.current[index] = null;
          clearIntervalRef(processId);
        }
        setError('Error: ' + response.message);
        completeActivityProcess(processId, 'error', response.message);

        var uiProcId = uiProcessIdRefs.current[index];
        if (uiProcId) {
          updateProcess(uiProcId, { status: 'error', error: response.message });
          setTimeout(function () { removeProcess(uiProcId); }, 5000);
          uiProcessIdRefs.current[index] = null;
        }

        setDownloadProgress(function (prev) {
          var updated = Object.assign({}, prev);
          delete updated[index];
          return updated;
        });

        downloadTriggeredRefs.current[index] = false;
        processIdRefs.current[index] = null;
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (processId, index, serverFileName) => {
    try {
      addLog(processId, 'Downloading file...');

      const source = reportEndpoints[index]?.source || 'general';
      const downloadEndpoint = source === 'pdf'
        ? API_ENDPOINTS.PDF_EXPORT_DOWNLOAD(processId)
        : API_ENDPOINTS.GENERAL_EXPORT_DOWNLOAD(processId);

      const response = await fetch(downloadEndpoint, {
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('jwtToken')
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = serverFileName || ('Report_' + Date.now() + '.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(processId, 'Download complete!');
      completeActivityProcess(processId, 'success', 'Export completed successfully');

      var uiProcId = uiProcessIdRefs.current[index];
      if (uiProcId) {
        updateProcess(uiProcId, { status: 'success' });
        setTimeout(function () { removeProcess(uiProcId); }, 2000);
        uiProcessIdRefs.current[index] = null;
      }

      if (addStructuredNotification) {
        try {
          await addStructuredNotification('EXCEL_DOWNLOAD_SUCCESS', {
            fileName: serverFileName,
            reportType: reportEndpoints[index]?.name || 'Report',
          });
        } catch (error) {
          console.error('Error sending success notification:', error);
        }
      }

      setDownloadProgress(function (prev) {
        var updated = Object.assign({}, prev);
        delete updated[index];
        return updated;
      });

      downloadTriggeredRefs.current[index] = false;
      processIdRefs.current[index] = null;
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(processId, 'Download error: ' + err.message);
      completeActivityProcess(processId, 'error', err.message);

      var uiProcId = uiProcessIdRefs.current[index];
      if (uiProcId) {
        updateProcess(uiProcId, { status: 'error', error: err.message });
        setTimeout(function () { removeProcess(uiProcId); }, 5000);
        uiProcessIdRefs.current[index] = null;
      }

      setDownloadProgress(function (prev) {
        var updated = Object.assign({}, prev);
        delete updated[index];
        return updated;
      });

      downloadTriggeredRefs.current[index] = false;
      processIdRefs.current[index] = null;
    }
  };

  const handleDownload = async (endpoint, fileName, index, resumeId) => {
    var uiProcessId = resumeId || null;
    const controller = new AbortController();

    const existingProcess = processes.find(function (p) {
      return (p.index === index || p.name === fileName) && (p.status === 'running' || p.status === 'pending');
    });
    if (existingProcess && !resumeId) return;

    if (resumeId) {
      updateProcess(resumeId, { status: 'running', controller: controller, index: index });
    } else {
      uiProcessId = addProcess({
        name: fileName,
        status: 'running',
        controller: controller,
        index: index,
        startTime: Date.now(),
      });
    }

    uiProcessIdRefs.current[index] = uiProcessId;
    downloadTriggeredRefs.current[index] = false;

    try {
      const response = await apiCall(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRefs.current[index] = serverProcessId;

        addActivityProcess(serverProcessId, 'Exporting ' + (reportEndpoints[index]?.name || 'Report'), 'generalexport');
        addLog(serverProcessId, 'Starting export...');

        setDownloadProgress(function (prev) {
          return Object.assign({}, prev, { [index]: 0 });
        });

        intervalRefs.current[index] = setInterval(function () { fetchStatus(serverProcessId, index); }, 1000);
        setIntervalRef(serverProcessId, intervalRefs.current[index]);
      } else {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const disposition = response.headers?.get('Content-Disposition');
        let serverFileName = fileName;
        if (disposition) {
          const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            serverFileName = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        const link = document.createElement('a');
        link.href = url;
        link.download = serverFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        updateProcess(uiProcessId, { status: 'success' });
        setTimeout(function () { removeProcess(uiProcessId); }, 2000);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        updateProcess(uiProcessId, { status: 'error', error: err.message });
        setError('Error downloading ' + fileName + ': ' + err.message);

        if (addStructuredNotification) {
          try {
            await addStructuredNotification('EXCEL_DOWNLOAD_FAILED', {
              fileName: fileName,
              reportType: reportEndpoints[index]?.name || 'Report',
              error: err.message
            }, true);
          } catch (error) {
            console.error('Error sending error notification:', error);
          }
        }
      }
      setTimeout(function () { removeProcess(uiProcessId); }, 5000);
    }
  };

  const handleCancel = (index) => {
    const process = processes.find(function (p) {
      return p.index === index && p.status === 'running';
    });
    if (process && process.controller) {
      process.controller.abort();
      removeProcess(process.id);
    }

    if (intervalRefs.current[index]) {
      clearInterval(intervalRefs.current[index]);
      intervalRefs.current[index] = null;
      if (processIdRefs.current[index]) {
        clearIntervalRef(processIdRefs.current[index]);
      }
    }

    setDownloadProgress(function (prev) {
      var updated = Object.assign({}, prev);
      delete updated[index];
      return updated;
    });
  };

  useEffect(() => {
    const resumeDownloads = async () => {
      const activeDownloads = processes.filter(function (p) { return p.status === 'pending'; });
      for (var i = 0; i < activeDownloads.length; i++) {
        var process = activeDownloads[i];
        var item = reportEndpoints[process.index];
        if (item) {
          await handleDownload(
            item.endpoint,
            item.name + '.' + item.extension,
            process.index,
            process.id
          );
        }
      }
    };
    setFiles(reportEndpoints);
    setLoading(false);
    resumeDownloads();
  }, [processes]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress sx={{ color: accentColor }} />
      </Box>
    );
  }

  const activeCount = Object.keys(downloadProgress).length;

  return (
    <Box sx={{ p: 3, width: '100%', maxWidth: 900, mx: 'auto', position: 'relative' }}>
      <InterfaceHeader
        title={sourceFilter === 'pdf' ? 'PDF Reports' : 'Reports'}
        color={accentColor}
        category={sourceFilter === 'pdf' ? 'pdf' : 'excel'}
      />

      {error && (
        <Alert severity="error" onClose={function () { setError(null); }} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats bar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        gap: 2,
        flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Chip
            icon={<InsertDriveFile sx={{ fontSize: 16 }} />}
            label={filteredReports.length + ' report' + (filteredReports.length !== 1 ? 's' : '')}
            size="small"
            sx={{
              bgcolor: alpha(accentColor, 0.1),
              color: accentColor,
              fontWeight: 600,
              borderRadius: '8px',
              '& .MuiChip-icon': { color: accentColor },
            }}
          />
          {activeCount > 0 && (
            <Chip
              icon={<CircularProgress size={12} sx={{ color: accentColor }} />}
              label={activeCount + ' processing'}
              size="small"
              sx={{
                bgcolor: alpha(accentColor, 0.08),
                color: accentColor,
                fontWeight: 600,
                borderRadius: '8px',
              }}
            />
          )}
        </Box>

        <TextField
          size="small"
          placeholder="Search reports..."
          value={searchQuery}
          onChange={function (e) { setSearchQuery(e.target.value); }}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: 13,
              '& fieldset': { borderColor: alpha(accentColor, 0.25) },
              '&:hover fieldset': { borderColor: alpha(accentColor, 0.5) },
              '&.Mui-focused fieldset': { borderColor: accentColor },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: alpha(accentColor, 0.5) }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={function () { setSearchQuery(''); }}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* Report cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {filteredReports.map(function (item, index) {
          var realIndex = reportEndpoints.indexOf(item);
          var fileName = item.name + '.' + item.extension;
          var process = processes.find(function (p) { return p.name === fileName; });
          var progress = downloadProgress[realIndex];
          var isRunning = process?.status === 'running';
          var isSuccess = process?.status === 'success';
          var IconComp = REPORT_ICONS[item.name] || DataObject;
          var lastUpdated = lastUpdatedDates[item.name];

          return (
            <Paper
              key={item.name}
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2.5,
                py: 2,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: isSuccess
                  ? alpha('#4caf50', 0.4)
                  : isRunning
                    ? alpha(accentColor, 0.4)
                    : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                bgcolor: isSuccess
                  ? alpha('#4caf50', 0.04)
                  : isRunning
                    ? alpha(accentColor, 0.04)
                    : isDark ? 'rgba(255,255,255,0.02)' : '#fff',
                transition: 'all 0.2s ease',
                cursor: isRunning ? 'default' : 'pointer',
                overflow: 'hidden',
                position: 'relative',
                '&:hover': {
                  borderColor: isRunning ? alpha(accentColor, 0.4) : alpha(accentColor, 0.5),
                  bgcolor: isRunning
                    ? alpha(accentColor, 0.04)
                    : alpha(accentColor, 0.06),
                  transform: isRunning ? 'none' : 'translateY(-1px)',
                  boxShadow: isRunning ? 'none' : ('0 4px 12px ' + alpha(accentColor, 0.15)),
                },
              }}
              onClick={function () {
                if (!isRunning) {
                  handleDownload(item.endpoint, fileName, realIndex);
                }
              }}
            >
              {/* Progress bar overlay at bottom */}
              {progress !== undefined && (
                <LinearProgress
                  variant={progress > 0 ? 'determinate' : 'indeterminate'}
                  value={progress}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    bgcolor: alpha(accentColor, 0.1),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: accentColor,
                    },
                  }}
                />
              )}

              {/* Icon */}
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(accentColor, isDark ? 0.15 : 0.08),
                mr: 2,
                flexShrink: 0,
              }}>
                <IconComp sx={{ fontSize: 20, color: accentColor }} />
              </Box>

              {/* Name + meta */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: theme.palette.text.primary,
                  lineHeight: 1.3,
                }}>
                  {item.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={'.' + item.extension}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: alpha(accentColor, 0.08),
                      color: accentColor,
                      borderRadius: '6px',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                  {lastUpdated && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Schedule sx={{ fontSize: 12, color: theme.palette.text.disabled }} />
                      <Typography sx={{
                        fontSize: 11,
                        color: theme.palette.text.secondary,
                      }}>
                        {lastUpdated}
                      </Typography>
                    </Box>
                  )}
                  {!lastUpdated && (
                    <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled, fontStyle: 'italic' }}>
                      Loading date...
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Right side: status or download icon */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
                {isRunning && progress !== undefined && progress > 0 && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: accentColor, minWidth: 36, textAlign: 'right' }}>
                    {Math.round(progress) + '%'}
                  </Typography>
                )}
                {isSuccess ? (
                  <CheckCircle sx={{ fontSize: 22, color: '#4caf50' }} />
                ) : isRunning ? (
                  <Tooltip title="Cancel download">
                    <IconButton
                      size="small"
                      onClick={function (e) {
                        e.stopPropagation();
                        handleCancel(realIndex);
                      }}
                      sx={{
                        color: accentColor,
                        '&:hover': { bgcolor: alpha(accentColor, 0.1) },
                      }}
                    >
                      <Stop sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Download sx={{
                    fontSize: 20,
                    color: isDark ? alpha(accentColor, 0.6) : alpha(accentColor, 0.4),
                    transition: 'color 0.2s',
                    '.MuiPaper-root:hover &': { color: accentColor },
                  }} />
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {filteredReports.length === 0 && searchQuery && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Search sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1 }} />
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>
            No reports matching "{searchQuery}"
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ExcelReports;
